/**
 * Influences (Steal from Masters) data store — the library's contents.
 * Mirrors snippets-store.ts's shape; panel open/close for the shared
 * clips/influences panel lives in modal-store ('snippets' key covers both
 * tabs — see SnippetsPanel).
 */
import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import type { Chord, Mode } from '@/types/chord';
import type { Influence } from '@/types/influence';
import { influenceStorage } from '@/services/influence-storage';
import { learnPiece as accumulatePiece } from '@/lib/steal/extract';
import { showSuccessToast, showErrorToast } from './toast-store';

const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
function numberWord(n: number): string {
  return NUMBER_WORDS[n] ?? String(n);
}

/** "one piece", "two pieces" — singular only at exactly 1. */
export function pieceCountPhrase(n: number): string {
  return `${numberWord(n)} piece${n === 1 ? '' : 's'}`;
}

interface LearnPieceParams {
  /** Existing influence to accumulate onto; omit to create a new one. */
  influenceId?: string;
  /** Required when influenceId is omitted. */
  newInfluenceName?: string;
  chords: Chord[];
  mode: Mode;
  pieceName: string;
}

interface InfluencesState {
  influences: Influence[];
  isLoading: boolean;
  error: string | null;

  loadInfluences: () => Promise<void>;
  deleteInfluence: (id: string) => Promise<void>;
  renameInfluence: (id: string, name: string) => Promise<void>;
  /** "Learn this piece" — create-or-accumulate, then toast. */
  learnPiece: (params: LearnPieceParams) => Promise<void>;
}

export const useInfluencesStore = create<InfluencesState>((set, get) => ({
  influences: [],
  isLoading: false,
  error: null,

  loadInfluences: async () => {
    set({ isLoading: true, error: null });
    try {
      const influences = await influenceStorage.getAll();
      set({ influences, isLoading: false });
    } catch (error) {
      console.error('Failed to load influences:', error);
      set({ error: 'Failed to load influences', isLoading: false });
    }
  },

  deleteInfluence: async (id) => {
    try {
      await influenceStorage.delete(id);
      await get().loadInfluences();
    } catch (error) {
      console.error('Failed to delete influence:', error);
      showErrorToast("couldn't delete that influence");
    }
  },

  renameInfluence: async (id, name) => {
    try {
      const influence = await influenceStorage.getById(id);
      if (!influence) return;
      await influenceStorage.save({ ...influence, name });
      await get().loadInfluences();
    } catch (error) {
      console.error('Failed to rename influence:', error);
      showErrorToast("couldn't rename that influence");
    }
  },

  learnPiece: async ({ influenceId, newInfluenceName, chords, mode, pieceName }) => {
    try {
      const existing = influenceId ? await influenceStorage.getById(influenceId) : null;
      const base: Influence = existing ?? {
        id: uuidv4(),
        name: newInfluenceName?.trim() || 'untitled influence',
        pieces: [],
        traits: [],
        createdAt: new Date().toISOString(),
      };

      const updated = accumulatePiece(base, chords, mode, pieceName);
      await influenceStorage.save(updated);
      await get().loadInfluences();

      showSuccessToast(`learned — ${pieceCountPhrase(updated.pieces.length)} now inside ${updated.name}`);
    } catch (error) {
      console.error('Failed to learn piece:', error);
      showErrorToast("couldn't learn that piece");
    }
  },
}));
